import { ref } from 'vue'
import axios from './Axios';
import offlineService from './OfflineService';

class MeApi {
    constructor() {
        this.isLoading = ref(false);
        this.permissions = ref([]);
        this.roleName = ref(null);
        this.roleId = ref(null);
        this.isSuperAdmin = ref(false);
        this.loaded = ref(false);

        this.storageKey = 'currentUserPermissions';

        if (!window.Api)
            window.Api = {};
        window.Api.MeAxi = axios;
        window.Api.Me = this;
    }

    createEmptyPermissions() {
        return {
            userId: null,
            subjectId: null,
            roleId: null,
            roleName: null,
            isSuperAdmin: false,
            permissions: []
        };
    }

    normalizePermissions(data) {
        if (!data)
            data = this.createEmptyPermissions();

        data.permissions = Array.isArray(data.permissions) ? data.permissions : [];
        data.isSuperAdmin = data.isSuperAdmin === true;

        return data;
    }

    storePermissions(data) {
        let normalized = this.normalizePermissions(data);
        localStorage.setItem(this.storageKey, JSON.stringify(normalized));
        this.permissions.value = normalized.permissions;
        this.roleName.value = normalized.roleName;
        this.roleId.value = normalized.roleId;
        this.isSuperAdmin.value = normalized.isSuperAdmin;
        this.loaded.value = true;
        return normalized;
    }

    getStoredPermissions() {
        let raw = localStorage.getItem(this.storageKey);
        if (!raw)
            return this.createEmptyPermissions();

        try {
            return this.normalizePermissions(JSON.parse(raw));
        } catch (e) {
            console.error("[ME] Failed to parse stored permissions", e);
            return this.createEmptyPermissions();
        }
    }

    async getPermissions(forceReload = false) {
        if (!forceReload && this.loaded.value) {
            return this.normalizePermissions({
                roleId: this.roleId.value,
                roleName: this.roleName.value,
                isSuperAdmin: this.isSuperAdmin.value,
                permissions: this.permissions.value
            });
        }

        if (offlineService.checkIsOffline || !navigator.onLine) {
            let stored = this.getStoredPermissions();
            this.storePermissions(stored);
            return stored;
        }

        this.isLoading.value = true;
        try {
            let result = (await axios.get('/api/me/permissions')).data;
            return this.storePermissions(result);
        } finally {
            this.isLoading.value = false;
        }
    }

    async ensurePermissionsLoaded() {
        return await this.getPermissions(false);
    }

    clearPermissions() {
        this.permissions.value = [];
        this.roleName.value = null;
        this.roleId.value = null;
        this.isSuperAdmin.value = false;
        this.loaded.value = false;
        localStorage.removeItem(this.storageKey);
    }

    hasPermissionSync(permissionKey) {
        if (this.isSuperAdmin.value)
            return true;
        const hasPermission = this.permissions.value.includes(permissionKey);
        console.log("[Me] hasPermmision", { permissionKey, hasPermission, permissions: this.permissions });
        return hasPermission;

    }

    hasAnyPermissionSync(permissionKeys) {
        if (this.isSuperAdmin.value)
            return true;
        return permissionKeys.some(p => this.permissions.value.includes(p));
    }

    hasAllPermissionsSync(permissionKeys) {
        if (this.isSuperAdmin.value)
            return true;
        return permissionKeys.every(p => this.permissions.value.includes(p));
    }

    async hasPermission(permissionKey) {
        await this.ensurePermissionsLoaded();
        return this.hasPermissionSync(permissionKey);
    }

    async hasAnyPermission(permissionKeys) {
        await this.ensurePermissionsLoaded();
        return this.hasAnyPermissionSync(permissionKeys);
    }

    async hasAllPermissions(permissionKeys) {
        await this.ensurePermissionsLoaded();
        return this.hasAllPermissionsSync(permissionKeys);
    }
}

let meApi = new MeApi();

export { meApi, MeApi }
export default meApi;